import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, ArrowLeft, ArrowRight, Package, ShareNetwork, Key, EnvelopeSimple, UserPlus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { itApi } from './itApi';
import { todayJst } from '@/utils/jstDate';

const STEPS = ['基本情報', '資産割当', 'IP予約', 'ライセンス', 'メール', '確認'];
const NONE = '__none__';

const SelectList = ({ items, selected, toggle, render, empty, testidPrefix }) => (
  items.length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center">{empty}</p> : (
    <ul className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
      {items.map((it) => (
        <li key={it.id}>
          <label data-testid={`${testidPrefix}-${it.id}`} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selected.has(it.id) ? 'border-teal-700 bg-blue-50/50' : 'border-zinc-200 hover:bg-zinc-50'}`}>
            <Checkbox checked={selected.has(it.id)} onCheckedChange={() => toggle(it.id)} />
            {render(it)}
          </label>
        </li>
      ))}
    </ul>
  )
);

export default function OnboardingWizard({ onCancel, onDone }) {
  const [step, setStep] = useState(1);
  const [empId, setEmpId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', department_id: '', join_date: todayJst() });

  const [departments, setDepartments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [emails, setEmails] = useState([]);

  const [assetIds, setAssetIds] = useState(() => new Set());
  const [licenseIds, setLicenseIds] = useState(() => new Set());
  const [vlanId, setVlanId] = useState('');
  const [emailMode, setEmailMode] = useState('none'); // none | link | create
  const [emailExistingId, setEmailExistingId] = useState('');
  const [emailNew, setEmailNew] = useState({ email: '', password: '' });

  useEffect(() => {
    itApi.departments().then(setDepartments).catch(() => {});
    itApi.assets({ assigned: 'unassigned' }).then((d) => setAssets(d.filter((a) => a.status !== 'disposed'))).catch(() => {});
    itApi.vlans().then(setVlans).catch(() => {});
    itApi.licenses().then((d) => setLicenses(d.filter((l) => l.seats_available === null || l.seats_available > 0))).catch(() => {});
    itApi.emails().then((d) => setEmails(d.filter((e) => !e.owner_employee_id && !e.is_shared))).catch(() => {});
  }, []);

  const toggle = (setter) => (id) => setter((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const next = async () => {
    if (step === 1) {
      if (!form.name.trim()) { toast.error('氏名を入力してください'); return; }
      if (!empId) {
        setBusy(true);
        try {
          const emp = await itApi.onboard({
            name: form.name.trim(),
            department_id: form.department_id && form.department_id !== NONE ? form.department_id : null,
            join_date: form.join_date || null,
          });
          setEmpId(emp.id);
        } catch (e) { toast.error(e?.response?.data?.detail || '社員の登録に失敗しました'); setBusy(false); return; }
        setBusy(false);
      }
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    if (!empId) { toast.error('社員が未登録です'); return; }
    setBusy(true);
    const errors = [];
    for (const id of assetIds) {
      try { await itApi.assignAsset(id, { employee_id: empId }); } catch { errors.push('資産割当'); }
    }
    if (vlanId && vlanId !== NONE) {
      try { await itApi.reserveNext(vlanId, { user: form.name.trim(), device_name: form.name.trim() }); }
      catch { errors.push('IP予約'); }
    }
    for (const id of licenseIds) {
      try { await itApi.assignSeat(id, { target_type: 'employee', target_id: empId }); } catch { errors.push('ライセンス'); }
    }
    if (emailMode === 'link' && emailExistingId) {
      try { await itApi.updateEmail(emailExistingId, { owner_employee_id: empId }); } catch { errors.push('メールリンク'); }
    } else if (emailMode === 'create' && emailNew.email.trim()) {
      try {
        await itApi.createEmail({ email: emailNew.email.trim(), password: emailNew.password || null, owner_employee_id: empId, status: 'active' });
      } catch (e) { errors.push('メール作成'); }
    }
    setBusy(false);
    if (errors.length) toast.warning(`一部の処理に失敗しました：${[...new Set(errors)].join('、')}`);
    else toast.success(`${form.name.trim()} さんのオンボーディングが完了しました`);
    onDone?.(empId);
  };

  const selDept = departments.find((d) => d.id === form.department_id);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl shadow-sm" data-testid="onboard-wizard">
      {/* Stepper */}
      <div className="px-5 md:px-6 py-4 border-b border-zinc-200">
        <div className="flex items-center gap-2 mb-3 text-teal-700 font-semibold">
          <UserPlus size={20} weight="duotone" /> オンボーディング
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const done = n < step; const active = n === step;
            return (
              <div key={label} className="flex items-center gap-1.5 shrink-0" data-testid={`onboard-step-${n}`}>
                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold transition-colors ${done ? 'bg-teal-700 text-white' : active ? 'bg-blue-100 text-teal-700 ring-2 ring-teal-700' : 'bg-zinc-100 text-zinc-400'}`}>
                  {done ? <Check size={13} weight="bold" /> : n}
                </span>
                <span className={`text-xs ${active ? 'text-teal-700 font-semibold' : 'text-zinc-400'} ${i < STEPS.length - 1 ? 'mr-1' : ''}`}>{label}</span>
                {i < STEPS.length - 1 && <span className="w-4 h-px bg-zinc-200" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 md:p-6 min-h-[300px]">
        {step === 1 && (
          <div className="space-y-4 max-w-md" data-testid="onboard-body-1">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">氏名 *</Label>
              <Input data-testid="onboard-name-input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="例: 山田 太郎" className="h-10 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">部署</Label>
              <Select value={form.department_id || NONE} onValueChange={(v) => setForm((p) => ({ ...p, department_id: v === NONE ? '' : v }))}>
                <SelectTrigger data-testid="onboard-dept-select" className="h-10 mt-1"><SelectValue placeholder="部署を選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>（未設定）</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">入社日</Label>
              <DateField testId="onboard-join-date" value={form.join_date || ''} onChange={(v) => setForm((p) => ({ ...p, join_date: v }))} className="mt-1" inputClassName="h-10" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboard-body-2">
            <p className="text-sm text-zinc-500 mb-3"><Package size={16} className="inline mr-1 text-blue-500" weight="duotone" />割り当てる備品・資産を選択（在庫のもの）</p>
            <SelectList items={assets} selected={assetIds} toggle={toggle(setAssetIds)} testidPrefix="onboard-asset" empty="割当可能な在庫資産がありません"
              render={(a) => (<span className="min-w-0"><span className="block text-sm font-medium text-zinc-800 truncate">{a.name}</span><span className="text-[11px] text-zinc-400 font-mono">{a.asset_tag || '—'} ・ {a.category || '—'}</span></span>)} />
          </div>
        )}

        {step === 3 && (
          <div className="max-w-md" data-testid="onboard-body-3">
            <p className="text-sm text-zinc-500 mb-3"><ShareNetwork size={16} className="inline mr-1 text-violet-500" weight="duotone" />固定IPが必要な場合、VLANを選ぶと次の空きIPを予約します（任意）</p>
            <Select value={vlanId || NONE} onValueChange={(v) => setVlanId(v === NONE ? '' : v)}>
              <SelectTrigger data-testid="onboard-vlan-select" className="h-10"><SelectValue placeholder="VLANを選択（任意）" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>（IPは予約しない）</SelectItem>
                {vlans.map((v) => <SelectItem key={v.id} value={v.id}>{v.name || v.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {step === 4 && (
          <div data-testid="onboard-body-4">
            <p className="text-sm text-zinc-500 mb-3"><Key size={16} className="inline mr-1 text-amber-500" weight="duotone" />座席に空きのあるライセンスを割当</p>
            <SelectList items={licenses} selected={licenseIds} toggle={toggle(setLicenseIds)} testidPrefix="onboard-license" empty="空き座席のあるライセンスがありません"
              render={(l) => (<span className="min-w-0"><span className="block text-sm font-medium text-zinc-800 truncate">{l.name}</span><span className="text-[11px] text-zinc-400">残り座席 {l.seats_available === null ? '∞' : l.seats_available}</span></span>)} />
          </div>
        )}

        {step === 5 && (
          <div className="max-w-md space-y-3" data-testid="onboard-body-5">
            <p className="text-sm text-zinc-500"><EnvelopeSimple size={16} className="inline mr-1 text-emerald-500" weight="duotone" />会社メールの割当（任意）</p>
            <Select value={emailMode} onValueChange={setEmailMode}>
              <SelectTrigger data-testid="onboard-email-mode" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">割り当てない</SelectItem>
                <SelectItem value="link">既存の未割当メールをリンク</SelectItem>
                <SelectItem value="create">新規メールを作成</SelectItem>
              </SelectContent>
            </Select>
            {emailMode === 'link' && (
              <Select value={emailExistingId} onValueChange={setEmailExistingId}>
                <SelectTrigger data-testid="onboard-email-existing" className="h-10"><SelectValue placeholder="メールを選択" /></SelectTrigger>
                <SelectContent>
                  {emails.length === 0 ? <SelectItem value="__empty__" disabled>未割当メールがありません</SelectItem>
                    : emails.map((e) => <SelectItem key={e.id} value={e.id}>{e.email}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {emailMode === 'create' && (
              <div className="space-y-2">
                <Input data-testid="onboard-email-new" value={emailNew.email} onChange={(e) => setEmailNew((p) => ({ ...p, email: e.target.value }))} placeholder="t.yamada@sensbey.co.jp" className="h-10" />
                <Input data-testid="onboard-email-pw" value={emailNew.password} onChange={(e) => setEmailNew((p) => ({ ...p, password: e.target.value }))} placeholder="初期パスワード（任意）" className="h-10 font-mono" />
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-3 max-w-md" data-testid="onboard-body-6">
            <h3 className="font-semibold text-zinc-800">確認</h3>
            <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-200 text-sm">
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">氏名</span><span className="font-medium text-zinc-800">{form.name}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">部署</span><span className="text-zinc-800">{selDept?.name || '未設定'}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">入社日</span><span className="text-zinc-800">{form.join_date || '—'}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">資産</span><span className="text-zinc-800">{assetIds.size} 件</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">IP予約</span><span className="text-zinc-800">{vlanId && vlanId !== NONE ? (vlans.find((v) => v.id === vlanId)?.name || vlans.find((v) => v.id === vlanId)?.key || '1件') : 'なし'}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">ライセンス</span><span className="text-zinc-800">{licenseIds.size} 件</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">メール</span><span className="text-zinc-800">{emailMode === 'none' ? 'なし' : emailMode === 'link' ? (emails.find((e) => e.id === emailExistingId)?.email || 'リンク') : (emailNew.email || '新規作成')}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 md:px-6 py-4 border-t border-zinc-200 flex justify-between gap-3">
        <Button variant="ghost" onClick={onCancel} data-testid="onboard-cancel-btn" className="text-zinc-500">キャンセル</Button>
        <div className="flex gap-2">
          {step > 1 && <Button variant="outline" onClick={back} disabled={busy} data-testid="onboard-back-btn"><ArrowLeft size={16} className="mr-1" /> 戻る</Button>}
          {step < STEPS.length
            ? <Button onClick={next} disabled={busy} data-testid="onboard-next-btn" className="bg-teal-700 hover:bg-teal-800">次へ <ArrowRight size={16} className="ml-1" /></Button>
            : <Button onClick={finish} disabled={busy} data-testid="onboard-finish-btn" className="bg-teal-700 hover:bg-teal-800">{busy ? '処理中…' : '完了する'}</Button>}
        </div>
      </div>
    </div>
  );
}
